import React, { useState, useEffect, useContext, useMemo } from 'react';
import HistoricalComparison from './HistoricalComparison';
import ConciergeHighlights from './ConciergeHighlights';
import RankHistoryGraph from './RankHistoryGraph';
import { FriendsContext } from '../contexts/FriendsContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { getBaseUrl } from '../utils/apiUtils';
import { useTournamentAppConfiguration } from '../utils/tournamentconfig/TournamentConfiguration';
import './ConciergeView.css';

const ConciergeView = ({ username, tournamentId }) => {
  const { theme } = useContext(ThemeContext);
  const { friendsList } = useContext(FriendsContext);
  const tournamentAppConfiguration = useTournamentAppConfiguration();

  const [historyList, setHistoryList] = useState([]);
  const [selectedTimestamp, setSelectedTimestamp] = useState('');
  const [historicalData, setHistoricalData] = useState(null);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [error, setError] = useState(null);

  // Generate list of all users (self + friends)
  const allUsers = useMemo(() => {
    const list = [];
    if (username) list.push(username);

    friendsList.forEach(friend => {
      if (friend.toLowerCase() !== username?.toLowerCase()) {
        list.push(friend);
      }
    });

    return list;
  }, [username, friendsList]);

  // Fetch the list of historical timestamps when tournamentId changes
  useEffect(() => {
    if (!tournamentId) {
      setHistoryList([]);
      return;
    }

    const fetchHistoryList = async () => {
      try {
        const url = `${getBaseUrl()}${tournamentAppConfiguration.endPoints.history}${tournamentId}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch history timestamps');
        const data = await response.json();

        if (data.success && data.history) {
          setHistoryList(data.history);

          // Load saved selection from localStorage
          try {
            const savedSelections = JSON.parse(localStorage.getItem('fli-pal-historical-selections') || '{}');
            const savedTimestamp = savedSelections[tournamentId];

            // Only use saved timestamp if it still exists in the history list
            if (savedTimestamp && data.history.some(h => h.timestamp === savedTimestamp)) {
              setSelectedTimestamp(savedTimestamp);
            } else if (data.history.length > 0) {
              // Default to oldest data (last in list) if no saved selection exists
              const oldestTimestamp = data.history[data.history.length - 1].timestamp;
              setSelectedTimestamp(oldestTimestamp);
            } else {
              setSelectedTimestamp('');
              setHistoricalData(null);
            }
          } catch (err) {
            console.error('Error loading saved historical selection:', err);
          }
        } else {
          setHistoryList([]);
        }
      } catch (err) {
        console.error('Error fetching history timestamps:', err);
        setHistoryList([]);
      }
    };

    fetchHistoryList();
  }, [tournamentId, tournamentAppConfiguration.endPoints.history]);

  // Fetch specific historical data when a timestamp is selected
  useEffect(() => {
    if (!selectedTimestamp || !tournamentId) {
      setHistoricalData(null);
      return;
    }

    const fetchHistoricalData = async () => {
      setIsFetchingHistory(true);
      setError(null);
      try {
        const url = `${getBaseUrl()}${tournamentAppConfiguration.endPoints.results}${tournamentId}/${selectedTimestamp}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch historical data');
        const data = await response.json();
        setHistoricalData(data);
      } catch (err) {
        console.error('Error fetching historical data:', err);
        setError('Failed to load historical data');
      } finally {
        setIsFetchingHistory(false);
      }
    };

    fetchHistoricalData();
  }, [selectedTimestamp, tournamentId, tournamentAppConfiguration.endPoints.results]);

  const handleTimestampChange = (e) => {
    const newTimestamp = e.target.value;
    setSelectedTimestamp(newTimestamp);

    // Save selection to localStorage
    try {
      const savedSelections = JSON.parse(localStorage.getItem('fli-pal-historical-selections') || '{}');
      if (newTimestamp) {
        savedSelections[tournamentId] = newTimestamp;
      } else {
        delete savedSelections[tournamentId];
      }
      localStorage.setItem('fli-pal-historical-selections', JSON.stringify(savedSelections));
    } catch (err) {
      console.error('Error saving historical selection:', err);
    }
  };

  const handleSelectMostRecent = () => {
    if (historyList.length > 0) {
      const mostRecent = historyList[0].timestamp;
      handleTimestampChange({ target: { value: mostRecent } });
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'N/A';

      let isoString = timestamp;
      if (timestamp.includes('T')) {
        const parts = timestamp.split('T');
        const datePart = parts[0].replace(/:/g, '-');
        let timePart = parts[1];

        const colons = (timePart.match(/:/g) || []).length;
        if (colons >= 3) {
          const lastColonIndex = timePart.lastIndexOf(':');
          timePart = timePart.substring(0, lastColonIndex) + '.' + timePart.substring(lastColonIndex + 1);
        }
        isoString = `${datePart}T${timePart}`;
      }

      const date = new Date(isoString);
      if (isNaN(date.getTime())) return timestamp;
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <div className={`concierge-view ${theme}`}>
      <div className="concierge-header-section">
        <h2>Concierge</h2>
        <p className="concierge-subtitle">Track movement for you and your friends.</p>

        {historyList.length > 0 && (
          <div className="global-historical-controls">
            <label htmlFor="global-timestamp-select">Compare everyone with past snapshot: </label>
            <div className="global-select-wrapper">
              <select
                id="global-timestamp-select"
                value={selectedTimestamp}
                onChange={handleTimestampChange}
                className={`timestamp-select ${theme}`}
              >
                <option value="">-- Select a snapshot --</option>
                {historyList.map(h => (
                  <option key={h.timestamp} value={h.timestamp}>
                    {formatDate(h.timestamp)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={`select-most-recent-button ${theme}`}
                onClick={handleSelectMostRecent}
                disabled={historyList.length === 0}
              >
                Latest
              </button>
            </div>
          </div>
        )}

        {isFetchingHistory && <div className="loading-history">Loading historical data...</div>}
        {error && <div className="error-history">{error}</div>}
        {historyList.length === 0 && !error && (
          <div className="info-history">No historical snapshots available for this tournament yet.</div>
        )}
      </div>

      <div className="concierge-grid">
        {historyList.length > 1 && (
          <RankHistoryGraph
            tournamentId={tournamentId}
            historyList={historyList}
            username={username}
            friendsList={friendsList}
            tournamentAppConfiguration={tournamentAppConfiguration}
          />
        )}
        {historicalData && (
          <ConciergeHighlights historicalData={historicalData} />
        )}

        {allUsers.map(user => (
          <HistoricalComparison
            key={user}
            username={user}
            historicalData={historicalData}
            isMe={user.toLowerCase() === username?.toLowerCase()}
          />
        ))}
        {allUsers.length === 0 && (
          <div className="no-users-message">
            Please search for a user or add friends to see historical comparisons.
          </div>
        )}
      </div>
    </div>
  );
};

export default ConciergeView;
